import type { Picked } from "../components/AddressAutocomplete";

type NominatimAddress = {
  amenity?: string;
  shop?: string;
  leisure?: string;
  building?: string;
  road?: string;
  house_number?: string;
  suburb?: string;
  city?: string;
  town?: string;
  village?: string;
};

type NominatimPlace = {
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  address?: NominatimAddress;
};

/**
 * Turn a Nominatim place into our canonical Picked shape. Shared between
 * the search autocomplete and the map picker so both produce identical
 * venue_name / address strings.
 */
export function nominatimToPicked(r: NominatimPlace): Picked {
  const a = r.address ?? {};
  // A named POI (cafe/shop/etc). Plain street points have no amenity, so we
  // don't fall back to r.name there — on a road Nominatim sets name = road,
  // which would duplicate the street line ("HaNevi'im, HaNevi'im 5").
  const placeName = a.amenity ?? a.shop ?? a.leisure ?? a.building;
  const street = [a.road, a.house_number].filter(Boolean).join(" ");
  const locality = a.suburb;
  const city = a.city ?? a.town ?? a.village;
  // Detailed, Google-Maps-pasteable line: POI name, street + number,
  // neighbourhood, city — drops the postal code / country / subdistrict
  // noise that display_name carries. De-duplicate the POI name when it just
  // repeats the street.
  const parts = [placeName, street, locality, city].filter(
    (p, i, arr): p is string => Boolean(p) && arr.indexOf(p) === i,
  );
  const detailed = parts.join(", ") || r.display_name;
  const venue = placeName ?? street ?? city ?? r.display_name.split(",")[0]?.trim() ?? "";
  return {
    display_name: r.display_name,
    venue_name: venue,
    address: detailed,
    lat: parseFloat(r.lat),
    lng: parseFloat(r.lon),
  };
}

async function reverseOnce(lat: number, lng: number, lang: string): Promise<Picked> {
  const url =
    "https://nominatim.openstreetmap.org/reverse" +
    "?format=json&addressdetails=1" +
    `&accept-language=${encodeURIComponent(lang)}` +
    `&lat=${lat}&lon=${lng}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`reverse geocode ${res.status}`);
  const data = (await res.json()) as NominatimPlace;
  // Trust the coordinate the user actually picked over Nominatim's snapped centre.
  return { ...nominatimToPicked(data), lat, lng };
}

/**
 * Reverse-geocode a coordinate to a Picked. Addresses always come back in
 * Hebrew: we're Israel-only, locals read it natively, and visitors can use
 * the per-card "Translate" button. Crucially this is a single Nominatim
 * request (soft limit ~1 req/sec) — the earlier Hebrew+UI two-request
 * version frequently hit the rate limit and silently returned nothing,
 * which made the pin sometimes fail to fill the address in.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<Picked> {
  return reverseOnce(lat, lng, "he");
}
